import React, { Fragment, FunctionComponent, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  StepperContent,
  StepperHeader,
  StepperSubmit
} from '../../../../../shared/general/stepper';
import { utilsHomeValueGetDefaultValuesStep4 } from '../../utils';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorsHomeValueFormFields,
  CalculatorsHomeValueResult,
  CalculatorsHomeValueStep4FormFields
} from '../../../../../../@types/features/calculators';
import { removeElement, scrollTo } from '../../../../../../utils';
import { useFormContext } from '../../../../../../hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCalculatorHomeValueStep4FormFields } from '../../config';
import { Heading } from '@chakra-ui/react';
import { homeValueUseResidenceValueHook } from '../../hooks';
import { HomeValueContext } from '../../context';
import { AppAlertDialog } from '../../../../../shared/general';
import LoaderDots from '../../../../../shared/general/loader-dots/loader-dots';
import { validationMessagesTranslated } from '../../../../../shared/validation/validation-messages';

export const CalculatorHomeValueStepperStep4: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const calculatorHomeValueStep4FormFields = useCalculatorHomeValueStep4FormFields(intl);
  const [completedText, setCompletedText] = useState<string>('');
  const [stepBusy, setStepBusy] = useState<boolean>(false);
  const { getPropertyValue } = homeValueUseResidenceValueHook();
  const { setPropertyValue } = useContext(HomeValueContext);
  const { form, updateForm, updateResult } = useFormContext<
    CalculatorsHomeValueResult,
    CalculatorsHomeValueFormFields
  >();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsHomeValueGetDefaultValuesStep4(intl)
  });

  const onSubmit = async (formData: Record<keyof CalculatorsHomeValueStep4FormFields, string>) => {
    const translationKey = `step4.propertyPlans.${formData.propertyPlans}`;
    setStepBusy(true);
    setCompletedText(intl.formatMessage({ id: translationKey, defaultMessage: '' }));
    const res = await getPropertyValue({
      ...form,
      ...formData
    } as unknown as CalculatorsHomeValueFormFields);
    updateForm(formData);
    setPropertyValue(res);
    updateResult({ housePrice: res.predictedValue });
    setStepBusy(false);
    scrollTo('.react-tools');
    removeElement('.c-cta-sticky');
  };

  return (
    <Fragment>
      <AppAlertDialog open={stepBusy} mode={`message`}>
        <div className={`text-center pt-4`}>
          <h3>
            <FormattedMessage
              id={'step4.busyMessage'}
              defaultMessage={`We zijn nu de waarde van de woning aan het berekenen`}
            />
          </h3>
          <LoaderDots width={100} height={75} />
        </div>
      </AppAlertDialog>
      <StepperHeader title={title} id={id} completedText={completedText} />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Heading size={`md`}>
            <FormattedMessage
              id={`step4.residenceType.title`}
              defaultMessage={`Wat is de reden van de waardebepaling? `}
            />
          </Heading>
          <FormElementDynamicInputTyped<CalculatorsHomeValueStep4FormFields>
            formFields={calculatorHomeValueStep4FormFields}
            id={'propertyPlans'}
            register={register}
            errors={errors}
            values={watch()}
            validation={{ required: validationMessagesTranslated(intl).required }}
          />
          <div className={`flex justify-center my-10`}>
            <StepperSubmit
              label={intl.formatMessage({
                id: 'step4.submit',
                defaultMessage: 'Woningwaarde berekenen'
              })}
              variant={`button`}
              colorScheme={'orange'}
              isLoading={stepBusy}
              id={id}
              isValidated={isValid}
            />
          </div>
        </form>
      </StepperContent>
    </Fragment>
  );
};
